import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BBALoader from '../../components/BBALoader';
import { Link, useLocation, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import BackButton from '../../components/BackButton';
import AlaCarteDeliverableRowsEditor from '../../components/crm/AlaCarteDeliverableRowsEditor';
import { useAuth } from '../../contexts/AuthContext';
import { getUserData, CRM_OWNER_EXECUTIVE_ROLES } from '../../services/accessControlService';
import { normalizeAlaCarteItem, PRICING_STATUS } from '../../utils/alaCarteCatalog';
import {
  normalizeAlaCarteBid,
  BID_STATUS_LABELS,
  accountOverviewPath,
  bidAccountTypeLabel,
  isInternalBid,
} from '../../utils/alaCarteBid';
import {
  buildTeamWorkspaceUpdatePayload,
  teamPricingUiModeFromPricingType,
} from '../../utils/alaCarteCatalogDraft';
import { teamAlaCarteListKindFromPath, teamAlaCartePaths } from '../../utils/teamAlaCarteRoutes';
import { Search, Building2 } from 'lucide-react';
import PortalBackground from '../../components/PortalBackground';

const STATUS_LABELS = {
  [PRICING_STATUS.draft]: 'Draft',
  [PRICING_STATUS.unpromoted]: 'Unpromoted',
  [PRICING_STATUS.in_review]: 'In review',
  [PRICING_STATUS.awaiting_team]: 'Awaiting team',
  [PRICING_STATUS.team_submitted]: 'Team submitted',
  [PRICING_STATUS.exec_ready]: 'Exec review',
  [PRICING_STATUS.published]: 'Published',
};

function statusBadgeClass(status) {
  switch (status) {
    case PRICING_STATUS.published:
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
    case PRICING_STATUS.awaiting_team:
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100';
    case PRICING_STATUS.team_submitted:
    case PRICING_STATUS.exec_ready:
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
}

const RECURRING_UNITS = ['Week', 'Month', 'Quarter', 'Year'];
const ONE_OFF_UNITS = ['item', 'project', 'hour', 'session', 'month', 'One Time'];
const CASE_UNITS = ['item', 'project', 'hour', 'engagement', 'month'];

function rowsMeaningful(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((r) => String(r?.quantity || '').trim() || String(r?.description || '').trim());
}

/**
 * @param {{ department: 'Marketing'|'Finance'|'HR' }} props
 */
export default function TeamAlaCarteItemWorkspace({ department }) {
  const { itemId } = useParams();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { queue: queuePath, catalog: catalogPath } = teamAlaCartePaths(department);
  const listKind = teamAlaCarteListKindFromPath(location.pathname) || 'queue';
  const isBidMode = listKind === 'queue';
  const backTo = listKind === 'catalog' ? catalogPath : queuePath;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [item, setItem] = useState(null);
  const [isExec, setIsExec] = useState(false);
  const [allCatalogItems, setAllCatalogItems] = useState([]);
  const [parentSearch, setParentSearch] = useState('');

  const [icon, setIcon] = useState('📦');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState('');
  const [deliverableRows, setDeliverableRows] = useState([]);
  const [internalNotes, setInternalNotes] = useState('');
  const [internalCostEstimate, setInternalCostEstimate] = useState('');
  const [pricingMode, setPricingMode] = useState('caseByCase');
  const [parentItemId, setParentItemId] = useState('');
  const [standardCost, setStandardCost] = useState('');
  const [standardUnit, setStandardUnit] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'alaCarteItems'));
        if (cancelled) return;
        setAllCatalogItems(snap.docs.map((d) => normalizeAlaCarteItem(d.id, d.data())));
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError('');
    try {
      const userData = await getUserData(currentUser?.uid);
      setIsExec(!!userData && CRM_OWNER_EXECUTIVE_ROLES.includes(userData.role));

      const collectionName = isBidMode ? 'alaCarteBids' : 'alaCarteItems';
      const snap = await getDoc(doc(db, collectionName, itemId));
      if (!snap.exists()) {
        setError(isBidMode ? 'Bid not found' : 'Item not found');
        setItem(null);
        return;
      }
      const normalized = isBidMode
        ? normalizeAlaCarteBid(snap.id, snap.data())
        : normalizeAlaCarteItem(snap.id, snap.data());
      if (normalized.department !== department) {
        setError(`This item is not in the ${department} department.`);
        setItem(null);
        return;
      }
      setItem(normalized);
      setIcon(normalized.icon || '📦');
      setName(normalized.name || '');
      setDescription(normalized.description || '');
      const ti = normalized.teamInput || {};
      const topScope = typeof normalized.scope === 'string' ? normalized.scope : '';
      const teamScope = typeof ti.scope === 'string' ? ti.scope : '';
      setScope(topScope.trim() ? topScope : teamScope);

      const topRows = Array.isArray(normalized.deliverableRows) ? normalized.deliverableRows : [];
      const teamRows = Array.isArray(ti.deliverableRows) ? ti.deliverableRows : [];
      if (rowsMeaningful(topRows)) {
        setDeliverableRows(topRows);
      } else if (rowsMeaningful(teamRows)) {
        setDeliverableRows(teamRows);
      } else {
        setDeliverableRows([{ quantity: '', description: '' }]);
      }

      setInternalNotes(normalized.internalOnly?.notes || '');
      {
        const tiEst = ti.internalCostEstimate;
        setInternalCostEstimate(tiEst !== '' && tiEst !== undefined && tiEst !== null ? String(tiEst) : '');
      }
      setPricingMode(teamPricingUiModeFromPricingType(normalized.pricingType));
      setParentItemId(normalized.parentItemId || '');
      const sp = normalized.standardPricing || {};
      setStandardCost(sp.cost !== undefined && sp.cost !== null && sp.cost !== '' ? String(sp.cost) : '');
      {
        const u = sp.unit || (normalized.pricingType === 'term' ? 'Month' : 'item');
        if (normalized.pricingType === 'term' && !RECURRING_UNITS.includes(u)) {
          setStandardUnit('Month');
        } else {
          setStandardUnit(u);
        }
      }
      setParentSearch('');
    } catch (e) {
      console.error(e);
      setError('Failed to load');
      setItem(null);
    } finally {
      setLoading(false);
    }
  }, [itemId, currentUser, department, isBidMode]);

  useEffect(() => {
    load();
  }, [load]);

  const parentEligibleItems = useMemo(
    () =>
      allCatalogItems.filter(
        (row) => row.pricingType !== 'child' && row.id && row.id !== itemId && row.id !== item?.catalogItemId
      ),
    [allCatalogItems, itemId, item?.catalogItemId]
  );

  const filteredParents = useMemo(
    () =>
      parentEligibleItems.filter((row) =>
        (row.name || '').toLowerCase().includes((parentSearch || '').toLowerCase())
      ),
    [parentEligibleItems, parentSearch]
  );

  const fieldsReadOnly = useMemo(() => {
    if (!item) return true;
    if (item.pricingStatus === PRICING_STATUS.published) return true;
    if (isExec) return false;
    return (
      item.pricingStatus === PRICING_STATUS.team_submitted ||
      item.pricingStatus === PRICING_STATUS.exec_ready
    );
  }, [item, isExec]);

  const canSaveDraft = useMemo(() => {
    if (!item) return false;
    if (item.pricingStatus === PRICING_STATUS.published) return false;
    if (isExec) return true;
    return (
      item.pricingStatus === PRICING_STATUS.awaiting_team || item.pricingStatus === PRICING_STATUS.draft
    );
  }, [item, isExec]);

  const canSubmit = useMemo(() => {
    if (!item || isExec) return false;
    return item.pricingStatus === PRICING_STATUS.awaiting_team;
  }, [item, isExec]);

  const workspacePayload = () => ({
    name,
    description,
    icon,
    scope,
    deliverableRows,
    internalOnlyNotes: internalNotes,
    internalCostEstimate,
    pricingMode,
    parentItemId,
    standardCost,
    standardUnit,
    existingStandardPricing: item.standardPricing || {},
  });

  const saveDraft = async () => {
    if (!item || !canSaveDraft) return;
    const built = buildTeamWorkspaceUpdatePayload(workspacePayload());
    if (!built.ok) {
      alert(built.message);
      return;
    }
    setSaving(true);
    try {
      const collectionName = isBidMode ? 'alaCarteBids' : 'alaCarteItems';
      await updateDoc(doc(db, collectionName, item.id), built.data);
      await load();
    } catch (e) {
      console.error(e);
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const submitForReview = async () => {
    if (!item || !canSubmit) return;
    const built = buildTeamWorkspaceUpdatePayload(workspacePayload());
    if (!built.ok) {
      alert(built.message);
      return;
    }
    setSaving(true);
    try {
      const collectionName = isBidMode ? 'alaCarteBids' : 'alaCarteItems';
      await updateDoc(doc(db, collectionName, item.id), {
        ...built.data,
        pricingStatus: PRICING_STATUS.team_submitted,
        teamSubmittedAt: new Date(),
      });
      await load();
    } catch (e) {
      console.error(e);
      alert('Submit failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24 relative">
        <PortalBackground />
        <div className="relative"><BBALoader size={48} /></div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen px-4 py-12 relative">
        <PortalBackground />
        <div className="relative">
        <BackButton to={backTo} />
        <p className="mt-6 text-gray-600 dark:text-gray-400">{error || 'Not found'}</p>
        </div>
      </div>
    );
  }

  const accountPath = isBidMode ? accountOverviewPath(item) : null;
  const catalogOverviewId = isBidMode ? item.catalogItemId : item.id;
  const statusLabels = isBidMode ? BID_STATUS_LABELS : STATUS_LABELS;
  const neg = (isBidMode && item.bidNegotiation) || {};
  const hasNegotiation = !!(neg.counterClientPrice || neg.counterInternalCost || neg.counterNote);

  return (
    <div className="min-h-screen px-4 pb-10 pt-4 relative">
      <PortalBackground />
      <div className="relative mx-auto max-w-4xl">
        <header className="relative mb-8 flex min-h-[3rem] items-start justify-center">
          <div className="absolute left-0 top-0 z-10">
            <BackButton to={backTo} className="shrink-0" />
          </div>
          <div className="w-full max-w-2xl px-14 text-center">
            <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(item.pricingStatus)}`}>
                {statusLabels[item.pricingStatus] || item.pricingStatus}
              </span>
              {item.postedToTeam && (
                <span className="text-xs text-gray-500 dark:text-gray-400">Posted to: {item.postedToTeam}</span>
              )}
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white md:text-3xl">
              {icon} {name || 'Untitled item'}
            </h1>
            {accountPath && (
              <Link
                to={accountPath}
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-red-700 hover:underline dark:text-red-300"
              >
                <Building2 className="h-4 w-4" />
                {item.companyName || 'View account'}
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {bidAccountTypeLabel(item.accountType)}
                </span>
              </Link>
            )}
            {isBidMode && isInternalBid(item) && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
                <Building2 className="h-4 w-4" />
                {item.companyName}
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  Internal
                </span>
              </p>
            )}
          </div>
        </header>

        {isExec && isBidMode && (
          <p className="mb-6 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:bg-blue-900/30 dark:text-blue-100">
            Owner/Executive: use{' '}
            <Link className="font-medium underline" to={`/crm/alacarte-bid/${item.id}`}>
              bid overview
            </Link>{' '}
            to accept, counter, or publish this account quote.
          </p>
        )}

        {isExec && !isBidMode && (
          <p className="mb-6 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:bg-blue-900/30 dark:text-blue-100">
            Owner/Executive: use{' '}
            <Link className="font-medium underline" to={`/crm/alacartetemplate/${item.id}`}>
              catalog overview
            </Link>{' '}
            to publish, manage packages, and Stripe IDs.
          </p>
        )}

        <div className="space-y-8">
          {isBidMode && hasNegotiation && (
            <section className="rounded-xl border border-purple-200 bg-purple-50/70 p-6 dark:border-purple-900/50 dark:bg-purple-900/10">
              <h2 className="mb-1 text-lg font-semibold text-purple-900 dark:text-purple-100">
                Exec response — please revise &amp; resubmit
              </h2>
              <p className="mb-4 text-sm text-purple-800 dark:text-purple-200">
                Leadership reviewed your last bid and asked for changes. Update the fields below, then submit again.
              </p>
              {(neg.counterClientPrice || neg.counterInternalCost) && (
                <dl className="grid gap-3 sm:grid-cols-2">
                  {neg.counterClientPrice && (
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-purple-700 dark:text-purple-300">
                        Target client price
                      </dt>
                      <dd className="mt-1 text-lg font-semibold text-purple-900 dark:text-purple-100">
                        {neg.counterClientPrice}
                      </dd>
                    </div>
                  )}
                  {neg.counterInternalCost && (
                    <div>
                      <dt className="text-xs font-medium uppercase tracking-wide text-purple-700 dark:text-purple-300">
                        Target internal cost
                      </dt>
                      <dd className="mt-1 text-lg font-semibold text-purple-900 dark:text-purple-100">
                        {neg.counterInternalCost}
                      </dd>
                    </div>
                  )}
                </dl>
              )}
              {neg.counterNote && (
                <p className="mt-4 whitespace-pre-wrap rounded-lg bg-white/70 p-3 text-sm text-gray-800 dark:bg-gray-900/40 dark:text-gray-200">
                  {neg.counterNote}
                </p>
              )}
            </section>
          )}
          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Identity</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Icon</label>
                <input
                  disabled={fieldsReadOnly}
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  className="w-full max-w-xs rounded-lg border border-gray-300 bg-white px-3 py-2 text-2xl disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                  maxLength={8}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Item name</label>
                <input
                  type="text"
                  disabled={fieldsReadOnly}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                <textarea
                  disabled={fieldsReadOnly}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                />
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Scope</h2>
            <textarea
              disabled={fieldsReadOnly}
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              placeholder="Scope for this offering"
            />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Deliverables</h2>
            <AlaCarteDeliverableRowsEditor
              rows={deliverableRows}
              onChange={setDeliverableRows}
              disabled={fieldsReadOnly}
            />
          </section>

          <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-6 dark:border-amber-900/50 dark:bg-amber-900/10">
            <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Internal only</h2>
            <textarea
              disabled={fieldsReadOnly}
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={4}
              className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 disabled:opacity-60 dark:border-amber-800 dark:bg-gray-900 dark:text-white"
              placeholder="CRM-only notes"
            />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">Pricing</h2>
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">Your team&apos;s proposed pricing model.</p>
            {pricingMode === 'complex' ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This item uses <strong>package or bundled pricing</strong>. Only Owner/Executive can change that
                structure in the{' '}
                <Link className="font-medium underline" to={`/crm/alacartetemplate/${catalogOverviewId}`}>
                  catalog overview
                </Link>
                . You can still update scope, deliverables, and internal notes above.
              </p>
            ) : (
              <>
                <div className="mb-4 flex flex-wrap gap-2">
                  {[
                    { id: 'recurring', label: 'Recurring' },
                    { id: 'oneOff', label: 'One-off' },
                    { id: 'child', label: 'Child' },
                    { id: 'caseByCase', label: 'Case by case' },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={fieldsReadOnly}
                      onClick={() => {
                        setPricingMode(opt.id);
                        if (opt.id === 'recurring' && !RECURRING_UNITS.includes(standardUnit)) setStandardUnit('Month');
                        if (opt.id === 'oneOff' && !ONE_OFF_UNITS.includes(standardUnit)) setStandardUnit('item');
                        if (opt.id === 'caseByCase' && !CASE_UNITS.includes(standardUnit)) setStandardUnit('item');
                      }}
                      className={`rounded-full px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${
                        pricingMode === opt.id
                          ? 'bg-red-600 text-white'
                          : 'border border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {(pricingMode === 'recurring' || pricingMode === 'oneOff' || pricingMode === 'caseByCase') && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {isBidMode ? 'Proposed client price' : 'Price'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        disabled={fieldsReadOnly}
                        value={standardCost}
                        onChange={(e) => setStandardCost(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                        placeholder={pricingMode === 'caseByCase' ? 'Optional' : ''}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Unit</label>
                      <select
                        disabled={fieldsReadOnly}
                        value={standardUnit}
                        onChange={(e) => setStandardUnit(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                      >
                        {(pricingMode === 'recurring' ? RECURRING_UNITS : pricingMode === 'oneOff' ? ONE_OFF_UNITS : CASE_UNITS).map(
                          (u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </div>
                )}

                {pricingMode === 'child' && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Parent item
                    </label>
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        disabled={fieldsReadOnly}
                        value={parentSearch}
                        onChange={(e) => setParentSearch(e.target.value)}
                        placeholder="Search parents…"
                        className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                      />
                    </div>
                    <select
                      disabled={fieldsReadOnly}
                      value={parentItemId}
                      onChange={(e) => setParentItemId(e.target.value)}
                      className="w-full max-w-xl rounded-lg border border-gray-300 bg-white px-3 py-2 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
                    >
                      <option value="">Select parent…</option>
                      {filteredParents.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.icon || '📦'} {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </section>

          {isBidMode && (
            <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-6 dark:border-amber-900/50 dark:bg-amber-900/10">
              <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">Internal cost estimate</h2>
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                What this will cost us to deliver. Used to calculate margin — clients never see this.
              </p>
              <div className="max-w-xs">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Internal cost (USD)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={fieldsReadOnly}
                  value={internalCostEstimate}
                  onChange={(e) => setInternalCostEstimate(e.target.value)}
                  className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 disabled:opacity-60 dark:border-amber-800 dark:bg-gray-900 dark:text-white"
                  placeholder="0.00"
                />
              </div>
            </section>
          )}

          {(item.postedAt || item.teamSubmittedAt) && (
            <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-600 shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300">
              {item.postedAt && (
                <p>
                  <span className="font-medium text-gray-800 dark:text-gray-100">Posted:</span>{' '}
                  {item.postedAt?.toDate ? item.postedAt.toDate().toLocaleString() : String(item.postedAt)}
                </p>
              )}
              {item.teamSubmittedAt && (
                <p className="mt-1">
                  <span className="font-medium text-gray-800 dark:text-gray-100">Team submitted:</span>{' '}
                  {item.teamSubmittedAt?.toDate
                    ? item.teamSubmittedAt.toDate().toLocaleString()
                    : String(item.teamSubmittedAt)}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-3">
            {canSaveDraft && (
              <button
                type="button"
                disabled={saving}
                onClick={saveDraft}
                className="rounded-lg bg-gray-800 px-5 py-2.5 text-white hover:bg-gray-900 disabled:opacity-50 dark:bg-gray-600"
              >
                Save draft
              </button>
            )}
            {canSubmit && (
              <button
                type="button"
                disabled={saving}
                onClick={submitForReview}
                className="rounded-lg bg-red-600 px-5 py-2.5 text-white hover:bg-red-700 disabled:opacity-50"
              >
                Submit for exec review
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
